import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../Config";
import moment from "moment";
import { capitalizeFirstLetter, truncateText } from "../../helpers";
import Tooltip from "../Tooltip";
import { MdDelete, MdEdit, MdModeEditOutline } from "react-icons/md";
import { FiMapPin } from "react-icons/fi";

const Table = ({
    apiData,
    setIsEditLocation,
    setSelectedData,
    setIsDeleteLocation
}) => {
    const storageKey = `property_location_list_page`;
    const [data, setData] = useState([
        {
            name: "",
            propertiesCount: "",
            createdAt: "",
            action: "",
        },
    ]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const editLocation = (item) => {
        setIsEditLocation(true);
        setSelectedData(item);
    };
    const deleteLocation = (item) => {
        setIsDeleteLocation(true);
        setSelectedData(item);
    };
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(savedPage);

    useEffect(() => {
        setCurrentPage(savedPage);
    }, [savedPage]);

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    const ActionBtns = (item) => (
        <div className="flex items-center gap-3">
            <Tooltip label="Click to edit the location">
                <button
                    onClick={() => editLocation(item)}
                    className="text-gray-400 hover:text-gray-700 transition bg-gray-50 rounded-full p-1.5"
                // title="Edit Location"
                >
                    <MdModeEditOutline size={16} />
                </button>
            </Tooltip>
            <Tooltip label="Click to delete the location">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        deleteLocation(item);
                    }}
                    className="text-gray-400 hover:text-rose-600 transition bg-gray-50 rounded-full p-1.5"
                >
                    <MdDelete size={16} />
                </button>
            </Tooltip>
        </div>
    );

    const getDescription = (item) => {
        if (!item?.description) {
            return <span>--</span>;
        }
        return (
            <span className="text-sm text-gray-500 line-clamp-2">
                {truncateText(item.description, 50)}
            </span>
        );
    };

    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => ({
                name: getName(item),
                createdAt: moment(item.createdAt).format('DD MMM, YYYY'),
                updatedAt: moment(item.updatedAt).format('DD MMM, YYYY'),
                description: getDescription(item),
                action: ActionBtns(item),
            }));
            setData(tempData);
        }
    }, [apiData, openActionMenu]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getName = (item) => {
        const name = capitalizeFirstLetter(item?.name || "N/A");

        return (
            <div className="flex items-start gap-2 min-w-0">
                <FiMapPin
                    size={14}
                    className="mt-[2px] text-gray-400 flex-shrink-0"
                />

                <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">
                        {name}
                    </span>

                    {/* <span className="text-xs text-gray-400">
                        Location
                    </span> */}
                </div>
            </div>

        );
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Name",
                    accessor: "name",
                },
                {
                    Header: "Created",
                    accessor: "createdAt",
                },
            ];
        }
        return [
            {
                Header: "Name",
                accessor: "name",
            },
            {
                Header: "Description",
                accessor: "description",
            },

            {
                Header: "Created At",
                accessor: "createdAt",
            },

            {
                Header: "Action",
                accessor: "action",
            },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        sessionStorage.setItem(storageKey, pageIndex);
    }, [pageIndex, storageKey]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(apiData?.length > 0).toString()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                            >
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <MobileDetails>
                                                <DetailItem>
                                                    <DetailLabel>Updated:</DetailLabel>
                                                    <DetailValue>{data[rowIndex].updatedAt}</DetailValue>
                                                </DetailItem>
                                                <DetailItem>
                                                    <DetailLabel>Action:</DetailLabel>
                                                    <DetailValue>{data[rowIndex].action}</DetailValue>
                                                </DetailItem>
                                            </MobileDetails>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2 text-sm text-gray-600">
                        Page{" "}
                        <span className="font-medium">
                            {pageIndex + 1} of {pageOptions.length}
                        </span>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75" />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const CustomTable = tw.table`w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const UserImageWrapper = tw.div`flex items-center gap-3 cursor-pointer`;
const UserImage = tw.img`w-10 h-10 rounded-full`;
const MobileDetails = tw.div`flex flex-col gap-3 p-4 bg-gray-50 rounded-lg`;
const DetailItem = tw.div`flex justify-between items-center`;
const DetailLabel = tw.span`text-sm text-gray-600 font-medium`;
const DetailValue = tw.span`text-sm text-gray-900`;

export default Table;